import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR, track } from "../lib/api";
import { useStore } from "../lib/store";
import { useSEO } from "../lib/seo";
import { Check, ChevronRight, ShieldCheck } from "lucide-react";
import MagicCheckoutButton from "../components/MagicCheckout";

const FIELDS = [
  ["name", "Full name", "text", true],
  ["phone", "Mobile number (10 digits)", "tel", true],
  ["email", "Email address", "email", true],
  ["address1", "Address line 1", "text", true],
  ["address2", "Address line 2 (optional)", "text", false],
  ["landmark", "Landmark (optional)", "text", false],
  ["pin", "PIN code (6 digits)", "tel", true],
  ["city", "City", "text", true],
  ["state", "State", "text", true],
];

export default function Checkout() {
  const { cart, subtotal, coupon, setCoupon, clearCart, settings } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => { try { return JSON.parse(sessionStorage.getItem("hh_checkout_form")) || {}; } catch { return {}; } });
  const [errors, setErrors] = useState({});
  const [pinInfo, setPinInfo] = useState(null);
  const [method, setMethod] = useState("prepaid");
  const [couponMsg, setCouponMsg] = useState("");
  const [placing, setPlacing] = useState(false);
  const [serverError, setServerError] = useState("");
  useSEO({ title: "Checkout — Haven Hives", description: "Complete your Haven Hives order." });

  useEffect(() => { sessionStorage.setItem("hh_checkout_form", JSON.stringify(form)); }, [form]);

  if (cart.length === 0 && !placing) {
    return (
      <div className="max-w-xl mx-auto text-center py-24 px-5" data-testid="checkout-empty">
        <h1 className="font-display text-3xl font-semibold">Nothing to check out yet</h1>
        <p className="text-charcoal/70 mt-2">Add a little joy to your bag first.</p>
        <Link to="/collections/shop-all" className="inline-block mt-6 bg-forest text-white hover:bg-forest-dark px-8 py-3.5 rounded-full">Start shopping</Link>
      </div>
    );
  }

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Please enter your full name.";
    const phone = (form.phone || "").replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(phone)) e.phone = "Please enter a valid 10-digit Indian mobile number.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email || "")) e.email = "Please enter a valid email address.";
    if (!form.address1 || form.address1.trim().length < 5) e.address1 = "Please enter your complete address.";
    if (!/^\d{6}$/.test(form.pin || "")) e.pin = "Please enter a valid 6-digit PIN code.";
    if (!form.city?.trim()) e.city = "Please enter your city.";
    if (!form.state?.trim()) e.state = "Please enter your state.";
    setErrors(e);
    return e;
  };

  const checkPin = async (pin) => {
    if (!/^\d{6}$/.test(pin || "")) return;
    try {
      const r = await api.post("/shipping/pin-check", { pin });
      setPinInfo(r.data);
      if (r.data.serviceable && !form.city) setForm((f) => ({ ...f, city: r.data.city, state: r.data.state }));
    } catch { setPinInfo(null); }
  };

  const codAvailable = pinInfo?.serviceable && pinInfo?.cod;
  const shippingEstimate = method === "cod" ? (settings?.shipping?.cod_flat ?? 79) : subtotal >= (settings?.shipping?.free_above ?? 1299) ? 0 : (settings?.shipping?.prepaid_flat ?? 49);
  const total = Math.max(subtotal - (coupon?.preview || 0) + shippingEstimate, 0);

  const applyCoupon = async () => {
    setCouponMsg("");
    try {
      const r = await api.post("/coupons/validate", { code: coupon?.code || "" });
      let preview = 0;
      if (subtotal >= r.data.min_order) {
        preview = r.data.type === "percent" ? Math.min(Math.round((subtotal * r.data.value) / 100), r.data.max_discount || Infinity) : r.data.value;
        setCoupon({ code: r.data.code, preview });
        setCouponMsg(`${r.data.code} applied — you save ${formatINR(preview)}.`);
      } else {
        setCouponMsg(`This code needs a minimum order of ${formatINR(r.data.min_order)}.`);
      }
    } catch (e) { setCouponMsg(e.response?.data?.detail || "That code isn't valid."); }
  };

  const placeOrder = async () => {
    setServerError("");
    if (Object.keys(validate()).length) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setPlacing(true);
    try {
      const payload = {
        items: cart.map((l) => ({ product_id: l.product_id, variant_id: l.variant_id, qty: l.qty })),
        address: { ...form, phone: (form.phone || "").replace(/\D/g, "").slice(-10), whatsapp_opt_in: form.whatsapp_opt_in !== false },
        payment_method: method,
        coupon: coupon?.preview ? coupon.code : null,
      };
      const r = await api.post("/checkout", payload);
      track("add_shipping_info", { value: r.data.totals?.total, currency: "INR" });
      if (r.data.payment.mode === "cod") {
        clearCart(); sessionStorage.removeItem("hh_checkout_form");
        navigate(`/order-confirm/${r.data.order_number}?token=${r.data.verify_token}`);
      } else if (r.data.payment.mode === "test-simulator") {
        window.location.href = r.data.payment.redirect;
      } else if (r.data.payment.mode === "razorpay") {
        openRazorpay(r.data);
      }
    } catch (e) {
      setServerError(e.response?.data?.detail || "We couldn't place your order. Please try again.");
      setPlacing(false);
    }
  };

  const openRazorpay = (data) => {
    const launch = () => {
      const rzp = new window.Razorpay({
        key: data.payment.key_id,
        amount: data.payment.amount,
        currency: "INR",
        name: "Haven Hives",
        description: `Order ${data.order_number}`,
        order_id: data.payment.razorpay_order_id,
        prefill: { name: form.name, email: form.email, contact: `+91${(form.phone || "").replace(/\D/g, "").slice(-10)}` },
        notes: { order_number: data.order_number },
        theme: { color: "#072835" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", resp);
            clearCart(); sessionStorage.removeItem("hh_checkout_form");
            window.location.href = `/order-confirm/${data.order_number}?tx_id=${data.payment.tx_id}`;
          } catch {
            window.location.href = `/payment/success?tx_id=${data.payment.tx_id}`;
          }
        },
        modal: { ondismiss: () => { window.location.href = `/payment/success?tx_id=${data.payment.tx_id}&dismissed=1`; } },
      });
      rzp.on("payment.failed", () => setServerError("Payment didn't go through — you can retry with another method."));
      rzp.open();
      setPlacing(false);
    };
    if (window.Razorpay) return launch();
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = launch;
    script.onerror = () => { setServerError("Couldn't load the payment gateway. Please check your connection and try again."); setPlacing(false); };
    document.body.appendChild(script);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="checkout-page">
      <h1 className="font-display font-semibold text-3xl sm:text-4xl">Checkout</h1>
      <p className="text-sm text-charcoal/60 mt-2">Guest checkout — no account needed. <span className="hidden sm:inline">You'll get a link to track your order.</span></p>
      {serverError && <p role="alert" className="mt-4 bg-coral/10 text-coral rounded-2xl p-4 text-sm font-semibold" data-testid="checkout-error">{serverError}</p>}

      <div className="grid lg:grid-cols-3 gap-10 mt-8 items-start">
        <form className="lg:col-span-2 space-y-5" onSubmit={(e) => { e.preventDefault(); placeOrder(); }} noValidate data-testid="checkout-form">
          <div className="bg-butter/60 rounded-3xl card-outline p-5">
            <MagicCheckoutButton />
            <div className="flex items-center gap-3 mt-4 text-[11px] font-bold uppercase tracking-widest text-charcoal/40"><span className="flex-1 h-px bg-charcoal/15" />or fill in your details below<span className="flex-1 h-px bg-charcoal/15" /></div>
          </div>
          <fieldset className="bg-white rounded-3xl card-outline p-5">
            <legend className="font-display font-semibold text-lg px-2">Delivery details</legend>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              {FIELDS.map(([name, label, type, required]) => (
                <div key={name} className={["address1", "address2", "landmark"].includes(name) ? "sm:col-span-2" : ""}>
                  <label htmlFor={`f-${name}`} className="text-xs font-bold uppercase tracking-widest text-charcoal/60">{label}{required && " *"}</label>
                  <input id={`f-${name}`} type={type} value={form[name] || ""} required={required}
                    inputMode={["phone", "pin"].includes(name) ? "numeric" : undefined} maxLength={name === "pin" ? 6 : name === "phone" ? 10 : undefined}
                    autoComplete={{ name: "name", phone: "tel-national", email: "email", address1: "address-line1", address2: "address-line2", pin: "postal-code", city: "address-level2", state: "address-level1" }[name]}
                    data-testid={`checkout-${name}-input`}
                    onChange={(e) => { setForm((f) => ({ ...f, [name]: e.target.value })); if (name === "pin" && e.target.value.length === 6) checkPin(e.target.value); }}
                    aria-invalid={!!errors[name]} aria-describedby={errors[name] ? `err-${name}` : undefined}
                    className={`mt-1.5 w-full border rounded-2xl px-4 h-12 text-base sm:text-sm ${errors[name] ? "border-coral" : "border-line"}`} />
                  {errors[name] && <p id={`err-${name}`} role="alert" className="text-xs text-coral mt-1" data-testid={`error-${name}`}>{errors[name]}</p>}
                  {name === "phone" && (
                    <label className="flex items-start gap-2 text-xs text-charcoal/70 mt-2 cursor-pointer">
                      <input type="checkbox" checked={form.whatsapp_opt_in !== false} onChange={(e) => setForm((f) => ({ ...f, whatsapp_opt_in: e.target.checked }))} data-testid="checkout-whatsapp-optin" className="mt-0.5 accent-forest" />
                      <span>Send order updates on <strong>WhatsApp</strong> to this number (confirmation, shipping, delivery). Untick for email/SMS only.</span>
                    </label>
                  )}
                  {name === "pin" && pinInfo && (
                    <p className="text-xs mt-1.5" data-testid="pin-checkout-result">
                      {pinInfo.serviceable
                        ? <span className="text-forest font-semibold"><Check className="w-3 h-3 inline" /> {pinInfo.note}</span>
                        : <span className="text-coral font-semibold">{pinInfo.message}</span>}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input type="checkbox" checked={form.gst_consent || false} onChange={(e) => setForm((f) => ({ ...f, gst_consent: e.target.checked }))} data-testid="gst-checkbox" />
                Add business GST details (optional)
              </label>
              {form.gst_consent && (
                <input value={form.gstin || ""} onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase().slice(0, 15) }))}
                  placeholder="GSTIN" aria-label="GSTIN" data-testid="gstin-input"
                  className="mt-2 w-full sm:w-64 border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
              )}
            </div>
          </fieldset>

          <fieldset className="bg-white rounded-3xl card-outline p-5">
            <legend className="font-display font-semibold text-lg px-2">Payment method</legend>
            <div className="space-y-3 mt-2">
              <label className={`flex items-start gap-3 border rounded-2xl p-4 cursor-pointer ${method === "prepaid" ? "border-forest bg-forest/5" : "border-charcoal/15"}`} data-testid="method-prepaid">
                <input type="radio" name="method" checked={method === "prepaid"} onChange={() => setMethod("prepaid")} className="mt-1 accent-[#072835]" />
                <span className="text-sm">
                  <span className="font-semibold block">Pay online — UPI, cards, net banking</span>
                  <span className="text-charcoal/60 text-xs">{settings?.integrations?.razorpay_configured ? "Secure Razorpay checkout — UPI, cards, net banking, wallets." : "Via our secure payment gateway."} {settings?.shipping && subtotal >= settings.shipping.free_above ? "Free shipping on this order." : `Flat ₹${settings?.shipping?.prepaid_flat ?? 49} shipping.`}</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 border rounded-2xl p-4 ${codAvailable ? "cursor-pointer" : "opacity-50"} ${method === "cod" ? "border-forest bg-forest/5" : "border-charcoal/15"}`} data-testid="method-cod">
                <input type="radio" name="method" checked={method === "cod"} disabled={!codAvailable} onChange={() => setMethod("cod")} className="mt-1 accent-[#072835]" />
                <span className="text-sm">
                  <span className="font-semibold block">Cash on Delivery {codAvailable ? "" : "(not available for this PIN)"}</span>
                  <span className="text-charcoal/60 text-xs">{codAvailable ? `${settings?.shipping?.cod_fee_note || "COD orders include a ₹30 handling fee."}` : "Check availability by entering your PIN code above."}</span>
                </span>
              </label>
            </div>
            <p className="text-[11px] text-charcoal/50 mt-3 flex items-center gap-1.5" data-testid="payment-mode-note"><ShieldCheck className="w-3.5 h-3.5" />
              {settings?.integrations?.razorpay_mode === "live"
                ? "Payments are processed securely by Razorpay. We never see or store your card details."
                : settings?.integrations?.razorpay_mode === "test"
                  ? "Razorpay is in TEST mode — use Razorpay's test UPI/cards; no real money moves."
                  : "Demonstration store — payments run in clearly-labelled test mode; no real money moves."}
            </p>
          </fieldset>

          <button type="submit" disabled={placing} data-testid="place-order-btn"
            className="w-full bg-forest text-white font-display text-base hover:bg-forest-dark py-4 rounded-full transition-colors disabled:opacity-60">
            {placing ? "Placing your order…" : method === "cod" ? `Place order · ${formatINR(total)}` : `Pay ${formatINR(total)} securely`}
          </button>
        </form>

        <aside className="bg-white rounded-3xl card-outline p-5 lg:sticky lg:top-24" aria-label="Order summary" data-testid="checkout-summary">
          <h2 className="font-display font-semibold text-xl">Order summary</h2>
          <ul className="mt-4 space-y-3">
            {cart.map((l) => (
              <li key={l.variant_id} className="flex gap-3 text-sm">
                <img src={l.image} alt={l.image_alt} width="48" height="48" className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                <div className="flex-1 min-w-0"><p className="font-semibold truncate">{l.name}</p><p className="text-xs text-charcoal/60">{l.variant_name} × {l.qty}</p></div>
                <span className="font-semibold">{formatINR(l.price * l.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex gap-2">
            <input value={coupon?.code || ""} onChange={(e) => setCoupon({ code: e.target.value.toUpperCase() })} placeholder="Coupon code" aria-label="Coupon code" data-testid="checkout-coupon-input" className="flex-1 min-w-0 border border-charcoal/15 rounded-full px-4 py-2.5 text-sm" />
            <button type="button" onClick={applyCoupon} data-testid="checkout-coupon-apply" className="bg-forest text-white text-sm font-semibold px-4 py-2.5 rounded-full">Apply</button>
          </div>
          {couponMsg && <p role="status" className="text-xs mt-2 text-charcoal/70">{couponMsg}</p>}
          <dl className="mt-4 space-y-2 text-sm border-t border-charcoal/10 pt-4">
            <div className="flex justify-between"><dt className="text-charcoal/70">Subtotal</dt><dd className="font-semibold">{formatINR(subtotal)}</dd></div>
            {coupon?.preview ? <div className="flex justify-between text-forest"><dt>Coupon {coupon.code}</dt><dd className="font-semibold">− {formatINR(coupon.preview)}</dd></div> : null}
            <div className="flex justify-between"><dt className="text-charcoal/70">Shipping ({method === "cod" ? "COD" : "prepaid"})</dt><dd>{shippingEstimate === 0 ? "Free" : formatINR(shippingEstimate)}</dd></div>
            <div className="flex justify-between text-base font-bold border-t border-charcoal/10 pt-3"><dt>Total payable</dt><dd data-testid="checkout-total">{formatINR(total)}</dd></div>
          </dl>
          <p className="text-xs text-charcoal/50 mt-2">{settings?.tax_wording || "Prices include GST."}</p>
        </aside>
      </div>
    </div>
  );
}
